import { lazy, Suspense, useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react'
import { ArrowRight, ChevronRight, Lock } from 'lucide-react'
import { format } from 'date-fns'

import type { Account, DomainLeaseList, EscrowDto } from '../../data/types'
import type { PaymentsSummaryDto } from '../../data/payments'
import {
  BLOCKS_PER_DAY,
  buildOverviewModel,
  type OverviewActivityItem,
  type OverviewAttentionItem,
  type OverviewInstanceSummary,
} from '../../data/overview'
import { BLOCK_SECONDS, formatUsdBalance, formatUsdBalanceCompact, formatUsdPerHour } from '../../core/pricing'
import type { Coin } from '../../core/types'
import type { WalletKind } from '../../core/wallet'
import { Button } from '../../ui/Button'
import { Card } from '../../ui/Card'
import { EmptyState, ErrorState, Skeleton } from '../../ui/Feedback'
import { Figure } from '../../ui/Figure'
import { Rate } from '../../ui/Rate'
import { StatusPill, type StatusTone } from '../../ui/StatusPill'
import type { QueryLike } from '../shared/DataGuard'
import { PageHeader } from '../shared/PageHeader'
import { ExampleMachines } from './ExampleMachines'
import { GuideSteps, guideComplete, guideProgress } from './GuideSteps'
import { EXAMPLE_MACHINES } from './examples'

/** Shared with the wallet bar, so opening it from either place costs one chunk. */
const WalletRecoverySheet = lazy(() =>
  import('../wallet/WalletRecoverySheet').then((module) => ({ default: module.WalletRecoverySheet })),
)

interface OverviewPageProps {
  account: Account | null
  /** True when a wallet is connected, even while its account query is loading. */
  walletConnected?: boolean
  accountLoading?: boolean
  accountError?: unknown
  onRetryAccount?: (() => void) | undefined
  leases: QueryLike<DomainLeaseList>
  escrow: QueryLike<EscrowDto>
  /** The chain height, for the deposit estimate behind the runway. Null when unknown. */
  chainHeight?: number | null | undefined
  /**
   * Billing's view of the holder: whether they are verified and what they hold.
   * It drives the Verify and Fund steps. Absent, those steps stay unmarked.
   */
  payments?: QueryLike<PaymentsSummaryDto> | undefined
  onConnect: (() => void) | undefined
  onNavigate?: ((path: string) => void) | undefined
  activity?: OverviewActivityItem[] | undefined
  /**
   * Connect a *named* extension. Preferred over `onConnect`, which cannot say
   * which wallet the user chose. Optional so the current route wiring keeps
   * compiling; see the handoff note for Agent 4.
   */
  onConnectWallet?: ((kind: WalletKind) => void) | undefined
  /**
   * Open the application's wallet sheet. Supplied on every real route, so the
   * workspace and the wallet bar raise the same one dialog; without it the page
   * falls back to its own, which is how it is rendered in isolation.
   */
  onOpenWallet?: (() => void) | undefined
  walletConnecting?: boolean
  walletError?: unknown
}

function navigate(onNavigate: OverviewPageProps['onNavigate'], path: string) {
  onNavigate?.(path)
}

/**
 * The single dominant action of a screen state, plan §5.
 *
 * One component for both states so that "Explore example machines" before a
 * wallet and "Start a rental" after one are visibly the same control in
 * the same place, rather than two designs that happen to sit at the same depth.
 */
function DominantAction({
  label,
  note,
  onClick,
}: {
  label: string
  note: string
  onClick: () => void
}) {
  return (
    <div className="overview-dominant">
      <Button variant="primary" size="lg" block onClick={onClick}>
        {label}
        <ArrowRight size={18} aria-hidden="true" />
      </Button>
      <p>{note}</p>
    </div>
  )
}

function SectionHead({ id, title, description, action }: { id: string; title: string; description: string; action?: ReactNode }) {
  return (
    <div className="overview-section-head">
      <div>
        <h2 id={id}>{title}</h2>
        <p>{description}</p>
      </div>
      {action}
    </div>
  )
}

type MetricTone = 'default' | 'caution' | 'critical'

/**
 * One stat tile.
 *
 * `soft` is for a value that is a state phrase ("Not spending", "Unavailable")
 * rather than a number, so it cannot be read as the answer at a glance. A tile
 * with `href` is one button: its name is the label and the value, and its
 * detail describes it. A loading tile is never a link.
 */
function MetricCard({
  label,
  value,
  detail,
  loading = false,
  tone = 'default',
  detailIcon,
  soft = false,
  href,
  onNavigate,
  title,
  meter,
}: {
  label: string
  value: string
  detail?: string | undefined
  loading?: boolean
  tone?: MetricTone
  detailIcon?: ReactNode
  soft?: boolean
  href?: string | undefined
  onNavigate?: OverviewPageProps['onNavigate']
  title?: string | undefined
  meter?: number | undefined
}) {
  const detailId = useId()
  const linked = Boolean(href) && !loading
  const body = (
    <>
      <span className="overview-metric-label">
        {label}
        {linked && <ChevronRight size={16} aria-hidden="true" />}
      </span>
      {loading ? (
        <Skeleton className="overview-stat-skeleton" />
      ) : (
        <strong className={`overview-metric-value${soft ? ' overview-metric-value--soft' : ''}`} title={title}>{soft ? value : <Figure text={value} />}</strong>
      )}
      {!loading && meter != null && (
        <span className="metric-meter" style={{ '--fill': `${Math.round(Math.min(1, Math.max(0, meter)) * 100)}%` } as CSSProperties} aria-hidden="true" />
      )}
      {!loading && detail && (
        <span id={detailId} className={`overview-metric-detail${tone === 'default' ? '' : ` overview-metric-detail--${tone}`}`}>
          {detailIcon}
          {detail}
        </span>
      )}
    </>
  )
  if (!linked) return <Card className="overview-metric-card">{body}</Card>
  return (
    <button
      type="button"
      className="ui-card ui-card-elev-1 overview-metric-card overview-metric-card--link"
      aria-label={`${label}, ${value}`}
      aria-describedby={detail ? detailId : undefined}
      onClick={() => navigate(onNavigate, href!)}
    >
      <span className="ui-card-body">{body}</span>
    </button>
  )
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/** The daily projection of a per-block rate, in dollars and cents. */
function dailyProjection(rate: Coin): string | null {
  try {
    return formatUsdBalanceCompact({ denom: rate.denom, amount: (BigInt(rate.amount) * BigInt(BLOCKS_PER_DAY)).toString() })
  } catch {
    return null
  }
}

function isPositive(coin: Coin | null): coin is Coin {
  return coin !== null && /^\d+$/.test(coin.amount) && BigInt(coin.amount) > 0n
}

function statusLabel(state: OverviewInstanceSummary['state']): string {
  switch (state) {
    case 'active':
      return 'Running'
    case 'insufficient-funds':
      return 'Out of funds'
    case 'closed':
      return 'Closed'
    default:
      return 'Unknown'
  }
}

function statusTone(state: OverviewInstanceSummary['state']): StatusTone {
  switch (state) {
    case 'active':
      return 'positive'
    case 'insufficient-funds':
      return 'critical'
    case 'unknown':
      return 'caution'
    default:
      return 'neutral'
  }
}

/**
 * A real instance, laid out on the same grid as an example row.
 *
 * The resemblance is deliberate and is the §4.5 requirement: connecting a
 * wallet replaces the content of the workspace, not the workspace.
 */
function InstanceRow({ instance, onNavigate }: { instance: OverviewInstanceSummary; onNavigate: OverviewPageProps['onNavigate'] }) {
  return (
    <li className="overview-instance-row">
      <div className="overview-instance-main">
        <div className="overview-instance-name">
          <strong>{instance.hostName}</strong>
          <p>{instance.region}</p>
        </div>
        <span className="overview-example-spec overview-instance-rate">
          <strong>{instance.rate.amount ? <Rate coin={instance.rate} /> : 'Unknown'}</strong>
          <span>Your rate</span>
        </span>
        <div className="overview-instance-actions">
          <StatusPill tone={statusTone(instance.state)} label={statusLabel(instance.state)} />
          <Button size="sm" onClick={() => navigate(onNavigate, `/app/instances/${encodeURIComponent(instance.dseq)}`)}>
            Open
          </Button>
        </div>
      </div>
    </li>
  )
}

function AttentionItem({ item, onNavigate }: { item: OverviewAttentionItem; onNavigate: OverviewPageProps['onNavigate'] }) {
  return (
    <li className={`overview-attention-item overview-attention-${item.severity}`}>
      <div>
        <strong>{item.title}</strong>
        <p>{item.body}</p>
      </div>
      {item.action && (
        <Button variant="link" size="sm" onClick={() => navigate(onNavigate, item.action!.path)}>
          {item.action.label}
        </Button>
      )}
    </li>
  )
}

function ActivityItem({ item }: { item: OverviewActivityItem }) {
  return (
    <li className="overview-activity-item">
      <div>
        <strong>{item.label}</strong>
        {item.detail && <p>{item.detail}</p>}
      </div>
      {item.at && <time dateTime={item.at}>{new Date(item.at).toLocaleDateString()}</time>}
    </li>
  )
}

function StaleNotice({ label, onRetry }: { label: string; onRetry?: (() => Promise<unknown> | void) | undefined }) {
  if (!onRetry) return <div className="stale-strip" role="status">Showing the last good {label} · refresh failed</div>
  return (
    <div className="stale-strip" role="status">
      Showing the last good {label} · refresh failed
      <Button size="sm" onClick={() => void onRetry()}>
        Try again
      </Button>
    </div>
  )
}

function QueryErrorState<T>({ query, title }: { query: QueryLike<T>; title: string }) {
  const body = query.error instanceof Error
    ? 'The console could not refresh this data. Try again in a moment.'
    : 'The service returned no explanation.'
  return <ErrorState title={title} body={body} onRetry={() => void query.refetch()} />
}

function AccountError({ onRetry }: { onRetry?: (() => void) | undefined }) {
  const body = 'The console could not load your account. Try again in a moment.'
  return <ErrorState title="We could not load your workspace" body={body} {...(onRetry ? { onRetry } : {})} />
}

function OverviewLoading() {
  return (
    <>
      <div className="stat-grid" aria-label="Loading workspace summary">
        {['Instances', 'Your rate', 'Prepaid balance', 'Runway'].map((label) => (
          <MetricCard key={label} label={label} value="" loading />
        ))}
      </div>
      <div className="overview-surface skeleton-stack overview-row-skeletons" aria-hidden="true">
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    </>
  )
}

export function OverviewPage({
  account,
  walletConnected = Boolean(account),
  accountLoading = false,
  accountError,
  onRetryAccount,
  leases,
  escrow,
  chainHeight = null,
  payments,
  onConnect,
  onNavigate,
  activity,
  onConnectWallet,
  onOpenWallet,
  walletConnecting = false,
  walletError,
}: OverviewPageProps) {
  const hostOwnsSheet = Boolean(onOpenWallet)
  const [walletOpen, setWalletOpen] = useState(false)
  // Opened at least once: keeps the sheet mounted so closing it returns focus.
  const [walletTouched, setWalletTouched] = useState(false)

  useEffect(() => { if (walletConnected) setWalletOpen(false) }, [walletConnected])

  const openWallet = onOpenWallet ?? (() => {
    setWalletTouched(true)
    setWalletOpen(true)
  })

  /**
   * Bridge to whichever connect callback the host supplied.
   *
   * `onConnectWallet` knows which extension the user picked. `onConnect` does
   * not, but it is what the current route passes, and calling it is still
   * correct — the host connects the extension it detected. Either way the
   * *choice* is presented by Agent 1's sheet rather than by a disabled button.
   */
  const connect = (kind: WalletKind) => {
    if (onConnectWallet) onConnectWallet(kind)
    else onConnect?.()
  }

  const hasInstance = (leases.data?.leases.length ?? 0) > 0
  const summary = payments?.data
  // Compliance is per tenant, so any linked wallet's summary answers Verify.
  // The balance is per wallet: billing reports the account's first-linked one,
  // and the leases above are the connected one's, so a balance on another
  // wallet must not mark Fund done for this one.
  const sameWallet = summary !== undefined && summary.wallet_address === account?.address
  const progress = guideProgress({
    walletConnected,
    verified: summary ? summary.compliance_status === 'approved' : undefined,
    funded: sameWallet ? (summary.available_cents ?? 0) > 0 : undefined,
    hasInstance,
  })
  // A connected guide is drawn only from settled facts. Drawn earlier it would
  // show a finished tenant "Verify" for a moment and then vanish, which reads
  // as the page changing its mind.
  const guideSettled =
    !walletConnected ||
    (!accountLoading &&
      (leases.data !== undefined || Boolean(leases.error)) &&
      !(payments?.isLoading ?? false))
  const showGuide = guideSettled && !guideComplete(progress)

  const header = walletConnected
    ? {
        title: 'Your workspace',
        description: 'What is running, what it costs, and what needs you.',
      }
    : {
        title: 'Set up your DDWS workspace',
        description: 'Five simple steps to go from zero to your first instance.',
      }

  return (
    <>
      <div className={walletConnected ? '' : 'overview-guide-header'}>
        <PageHeader eyebrow="Cloud workspace" title={header.title} description={header.description} />
      </div>

      {showGuide && (
        <GuideSteps
          progress={progress}
          onConnect={openWallet}
          onVerify={onNavigate ? () => navigate(onNavigate, '/app/billing') : undefined}
          onFund={onNavigate ? () => navigate(onNavigate, '/app/billing') : undefined}
        />
      )}

      {!walletConnected ? (
        <ExploreWorkspace onNavigate={onNavigate} />
      ) : accountLoading ? (
        <OverviewLoading />
      ) : accountError && !account ? (
        <AccountError onRetry={onRetryAccount} />
      ) : account ? (
        <ConnectedOverview
          account={account}
          accountError={accountError}
          onRetryAccount={onRetryAccount}
          leases={leases}
          escrow={escrow}
          chainHeight={chainHeight}
          onNavigate={onNavigate}
          activity={activity}
          guideSettled={guideSettled}
          showGuide={showGuide}
        />
      ) : (
        <OverviewLoading />
      )}

      {!hostOwnsSheet && walletTouched && (
        <Suspense fallback={null}>
          <WalletRecoverySheet
            open={walletOpen}
            onOpenChange={setWalletOpen}
            connecting={walletConnecting}
            error={walletError}
            connected={walletConnected}
            onConnect={connect}
          />
        </Suspense>
      )}
    </>
  )
}

/**
 * The email-only workspace: one thing to look at, one thing to do.
 *
 * There is no balance, no runway, no spend, and no instance list here, because
 * without a wallet the console knows none of those and inventing them would be
 * the exact dishonesty the plan forbids.
 */
function ExploreWorkspace({ onNavigate }: Pick<OverviewPageProps, 'onNavigate'>) {
  return (
    <>
      <ExampleMachines machines={EXAMPLE_MACHINES} />
      <DominantAction
        label="Explore example machines"
        note="You can connect a wallet anytime to rent and manage your own resources."
        onClick={() => navigate(onNavigate, '/app/rent')}
      />
    </>
  )
}

/**
 * The first thing a tenant with nothing running needs: where their money is and
 * the one move that starts something. It replaces the lower dominant action
 * rather than joining it, and it never competes with the guide.
 *
 * It does not say a new rental draws on the prepaid balance, because it cannot:
 * a rental's deposit comes from the wallet into a new escrow account. With
 * nothing running, any prepaid money sits in another open request's escrow, and
 * closing that request is how it comes back.
 */
function FirstRunBanner({ held, heldRequests, onNavigate }: { held: Coin | null; heldRequests: number; onNavigate: OverviewPageProps['onNavigate'] }) {
  const balance = isPositive(held) && heldRequests > 0 ? formatUsdBalanceCompact(held) : null
  return (
    <section className="ui-card ui-card-elev-1 overview-first-run" aria-labelledby="overview-first-run-title">
      <div>
        <h2 id="overview-first-run-title">Nothing is running</h2>
        <p>
          {balance
            ? `${balance} is held by ${plural(heldRequests, 'open request', 'open requests')}. Close it from Instances to get it back, or rent a machine.`
            : 'Rent a machine to get started. Your wallet signs the rental.'}
        </p>
      </div>
      <div className="overview-first-run-actions">
        {balance && (
          <Button onClick={() => navigate(onNavigate, '/app/instances')}>
            View instances
          </Button>
        )}
        <Button variant="primary" onClick={() => navigate(onNavigate, '/app/rent')}>
          Rent a machine
        </Button>
      </div>
    </section>
  )
}

function ConnectedOverview({
  account,
  accountError,
  onRetryAccount,
  leases,
  escrow,
  chainHeight,
  onNavigate,
  activity,
  guideSettled,
  showGuide,
}: {
  account: Account
  accountError?: unknown
  onRetryAccount?: (() => void) | undefined
  leases: QueryLike<DomainLeaseList>
  escrow: QueryLike<EscrowDto>
  chainHeight: number | null
  onNavigate: OverviewPageProps['onNavigate']
  activity?: OverviewActivityItem[] | undefined
  guideSettled: boolean
  showGuide: boolean
}) {
  const model = buildOverviewModel({
    account,
    leases: leases.data ?? null,
    escrow: escrow.data ?? null,
    height: chainHeight,
    activity,
  })
  const { stats } = model
  const leasesUnavailable = Boolean(leases.error && !leases.data)
  const escrowUnavailable = Boolean(escrow.error && !escrow.data)
  const leasesKnown = leases.data !== undefined
  const hasInstances = model.instances.length > 0
  // The panel promises machines running now, so a closed lease is history and
  // belongs on the instances page, not here. Out of funds and unknown stay:
  // each is still open on chain and has its own attention item.
  const activeInstances = model.instances.filter((instance) => instance.state !== 'closed')
  const running = stats.runningInstances
  const outOfFunds = model.instances.filter((instance) => instance.state === 'insufficient-funds').length
  const allHealthy = running > 0 && model.instances.every((instance) => instance.state === 'active' || instance.state === 'closed')

  // Every instance closed, not merely none running: an instance out of funds
  // or in an unknown state has its own attention item and its own next move,
  // and "Nothing is running" with Rent as the primary action would contradict it.
  const firstRun =
    guideSettled &&
    !showGuide &&
    leasesKnown &&
    !leases.error &&
    model.instances.every((instance) => instance.state === 'closed') &&
    (escrow.data !== undefined || Boolean(escrow.error))

  // Instances
  const instancesDetail = leasesUnavailable
    ? undefined
    : outOfFunds > 0
      ? `${outOfFunds} out of funds`
      : allHealthy
        ? 'All healthy'
        : running === 0 && stats.heldRequests > 0
          ? `${plural(stats.heldRequests, 'open request', 'open requests')}, not running`
          : undefined

  // Your rate
  const rateText = leasesUnavailable || stats.rateState === 'unavailable' ? null : formatUsdPerHour(stats.rate, BLOCK_SECONDS)
  const daily = stats.rateState === 'charging' && stats.rate ? dailyProjection(stats.rate) : null
  const rateDetail = leasesUnavailable
    ? undefined
    : rateText === null
      ? "A running instance did not report its rate."
      : stats.rateState === 'idle'
        ? 'Nothing is being charged'
        : daily
          ? `At this rate, ${daily} a day`
          : undefined

  // Prepaid balance
  const balanceText = escrowUnavailable ? null : formatUsdBalanceCompact(stats.prepaidBalance)
  const held = isPositive(stats.heldBalance) ? stats.heldBalance : null
  const heldBy = `${plural(stats.heldRequests, 'open request', 'open requests')}`
  const balanceDetail = balanceText === null || !leasesKnown
    ? undefined
    : held && running === 0
      ? `Held by ${heldBy}`
      : held
        ? `Includes ${formatUsdBalanceCompact(held)} held by ${heldBy}`
        : running > 0
          ? `Across ${plural(running, 'instance', 'instances')}`
          : undefined

  // Runway
  const runwayUnavailable = escrowUnavailable || leasesUnavailable || stats.runwayState === 'unavailable'
  const runwayKnown = !runwayUnavailable && stats.runwayState === 'known' && stats.runwayDays != null
  const runwayText = runwayUnavailable
    ? 'Unavailable'
    : stats.runwayState === 'not-spending'
      ? 'Not spending'
      : stats.runwayDays === 0
        ? 'Less than a day'
        : plural(stats.runwayDays ?? 0, 'day', 'days')
  const endsOn = stats.runwayEndsAt ? format(stats.runwayEndsAt, 'EEE d MMM') : null
  const runwayDetail = runwayUnavailable
    ? undefined
    : stats.runwayState === 'not-spending'
      ? 'Starts when an instance runs'
      : endsOn
        ? stats.runwayAlert
          ? `Add funds by about ${endsOn}`
          : `Until about ${endsOn}`
        : undefined

  return (
    <>
      {accountError && <StaleNotice label="account data" onRetry={onRetryAccount ? async () => { onRetryAccount() } : undefined} />}
      {leases.error && leases.data && <StaleNotice label="instance data" onRetry={() => leases.refetch()} />}
      {escrow.error && escrow.data && <StaleNotice label="balance data" onRetry={() => escrow.refetch()} />}

      {firstRun && <FirstRunBanner held={escrowUnavailable ? null : stats.heldBalance} heldRequests={stats.heldRequests} onNavigate={onNavigate} />}

      <div className="stat-grid">
        <MetricCard
          label="Instances"
          value={leasesUnavailable ? 'Unavailable' : String(running)}
          soft={leasesUnavailable}
          detail={instancesDetail}
          tone={outOfFunds > 0 ? 'caution' : 'default'}
          loading={leases.isLoading}
          href={leasesUnavailable ? undefined : '/app/instances'}
          onNavigate={onNavigate}
        />
        <MetricCard
          label="Your rate"
          value={rateText ?? 'Unavailable'}
          soft={rateText === null}
          detail={rateDetail}
          loading={leases.isLoading}
        />
        <MetricCard
          label="Prepaid balance"
          value={balanceText ?? 'Unavailable'}
          soft={balanceText === null}
          title={balanceText === null ? undefined : formatUsdBalance(stats.prepaidBalance) ?? undefined}
          detail={balanceDetail}
          tone={held && running === 0 ? 'caution' : 'default'}
          detailIcon={held && running === 0 ? <Lock size={14} aria-hidden="true" /> : undefined}
          loading={escrow.isLoading}
          href={balanceText === null ? undefined : '/app/billing'}
          onNavigate={onNavigate}
        />
        <MetricCard
          label="Runway"
          value={runwayText}
          soft={!runwayKnown}
          detail={runwayDetail}
          tone={runwayKnown && stats.runwayAlert ? stats.runwayAlert : 'default'}
          meter={runwayKnown && stats.runwayAlert ? (stats.runwayDays ?? 0) / 14 : undefined}
          loading={escrow.isLoading || leases.isLoading}
          href={runwayKnown ? '/app/billing' : undefined}
          onNavigate={onNavigate}
        />
      </div>

      <section className="overview-examples" aria-labelledby="overview-instances-title">
        <SectionHead
          id="overview-instances-title"
          title="Your instances"
          description="Machines running on your account right now."
          action={hasInstances ? <Button size="sm" onClick={() => navigate(onNavigate, '/app/instances')}>View all</Button> : undefined}
        />

        {leases.isLoading ? (
          <div className="overview-surface skeleton-stack overview-row-skeletons" aria-label="Loading instances">
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </div>
        ) : leasesUnavailable ? (
          <QueryErrorState title="No instance data" query={leases} />
        ) : !hasInstances ? (
          <EmptyState title="Nothing running yet" body="Instances you rent appear here, with their host, rate, and status." />
        ) : activeInstances.length === 0 ? (
          <EmptyState title="None is active" body="Your past instances are listed under View all." />
        ) : (
          <div className="overview-surface">
            <ul className="overview-example-list">
              {activeInstances.slice(0, 3).map((instance) => <InstanceRow key={instance.id} instance={instance} onNavigate={onNavigate} />)}
            </ul>
          </div>
        )}
      </section>

      {!firstRun && !hasInstances && !leases.isLoading && !leasesUnavailable && (
        <DominantAction
          label="Start a rental"
          note="Choose a host or describe what you need. Your wallet signs the rental."
          onClick={() => navigate(onNavigate, '/app/rent')}
        />
      )}

      <div className="overview-columns">
        <Card title="Needs attention">
          {escrowUnavailable ? (
            <QueryErrorState title="Balance data unavailable" query={escrow} />
          ) : model.attention.length > 0 ? (
            <ul className="overview-attention-list">
              {model.attention.map((item) => <AttentionItem key={item.id} item={item} onNavigate={onNavigate} />)}
            </ul>
          ) : leasesUnavailable ? (
            <p className="muted">Some attention checks are unavailable until the workspace refreshes.</p>
          ) : (
            <p className="muted">No urgent actions.</p>
          )}
        </Card>

        <Card title="Recent activity" action={<Button variant="link" size="sm" onClick={() => navigate(onNavigate, '/app/advanced/activity')}>View all</Button>}>
          {model.activity.length > 0 ? (
            <ul className="overview-activity-list">
              {model.activity.slice(0, 5).map((item) => <ActivityItem key={item.id} item={item} />)}
            </ul>
          ) : (
            <div className="overview-panel-empty">
              <p className="muted">No recent activity yet.</p>
              <Button variant="link" onClick={() => navigate(onNavigate, '/app/rent')}>Rent a machine</Button>
            </div>
          )}
        </Card>
      </div>
    </>
  )
}
